import type { ReactNode } from 'react';
import { Suspense, lazy } from 'react';
import { Navigate, createBrowserRouter, useLocation } from 'react-router-dom';
import { AppLayout } from '@/components/AppLayout';
import { LoginPage } from '@/features/auth/LoginPage';
import { WaitingApproval } from '@/features/auth/WaitingApproval';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { useAuth } from '@/hooks/useAuth';
import { useT } from '@/i18n/useT';

// Code-split everything off the first-paint path (issue #38 §3). Login, the tab
// layout and the dashboard (the landing) stay eager; the admin panel and the
// secondary / full-screen routes load on demand instead of riding in the main
// bundle every visitor downloads.
const CravingPage = lazy(() => import('@/features/craving/CravingPage').then((m) => ({ default: m.CravingPage })));
const EmergencyPage = lazy(() => import('@/features/emergency/EmergencyPage').then((m) => ({ default: m.EmergencyPage })));
const HealthPage = lazy(() => import('@/features/health/HealthPage').then((m) => ({ default: m.HealthPage })));
const JournalPage = lazy(() => import('@/features/journal/JournalPage').then((m) => ({ default: m.JournalPage })));
const LegalPage = lazy(() => import('@/features/legal/LegalPage').then((m) => ({ default: m.LegalPage })));
const MoneyPage = lazy(() => import('@/features/money/MoneyPage').then((m) => ({ default: m.MoneyPage })));
const MorePage = lazy(() => import('@/features/more/MorePage').then((m) => ({ default: m.MorePage })));
const NotificationsPage = lazy(() => import('@/features/settings/NotificationsPage').then((m) => ({ default: m.NotificationsPage })));
const AdminPage = lazy(() => import('@/features/admin/AdminPage').then((m) => ({ default: m.AdminPage })));
const SettingsPage = lazy(() => import('@/features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const OnboardingPage = lazy(() => import('@/features/onboarding/OnboardingPage').then((m) => ({ default: m.OnboardingPage })));
const ProgressPage = lazy(() => import('@/features/progress/ProgressPage').then((m) => ({ default: m.ProgressPage })));
const TreatmentPage = lazy(() => import('@/features/treatment/TreatmentPage').then((m) => ({ default: m.TreatmentPage })));
const StagePage = lazy(() => import('@/features/stage/StagePage').then((m) => ({ default: m.StagePage })));
// La landing solo la ve quien no tiene sesión, así que no viaja en el bundle de la app (#78).
const LandingPage = lazy(() => import('@/features/landing/LandingPage').then((m) => ({ default: m.LandingPage })));

function FullScreenLoader() {
  const t = useT();
  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg" role="status" aria-live="polite">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-line border-t-brand" />
      <span className="sr-only">{t('common.loading')}</span>
    </div>
  );
}

/** Suspense boundary for the lazy route components above. */
function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<FullScreenLoader />}>{children}</Suspense>;
}

/** Requires a session and a finished registration. */
function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, needsApproval, needsOnboarding, isLoading } = useAuth();

  if (isLoading) return <FullScreenLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // Private beta: unapproved accounts can't reach the app or onboarding (#28).
  if (needsApproval) return <WaitingApproval />;
  if (needsOnboarding) return <Navigate to="/onboarding" replace />;
  return <>{children}</>;
}

/**
 * La raíz reparte según la sesión (#78): quien ya usa la app entra directo, como siempre;
 * quien llega de fuera ve la landing en vez de un login que no le explica nada.
 *
 * Solo la raíz. Una ruta interna sin sesión (`/salud`) sigue yendo al login: enseñar la
 * portada a quien pidió una pantalla concreta sería perderle lo que buscaba.
 */
function RootGate() {
  const { isAuthenticated, isLoading } = useAuth();
  const isRoot = useLocation().pathname === '/';

  if (isLoading) return <FullScreenLoader />;
  if (!isAuthenticated && isRoot) {
    return (
      <Lazy>
        <LandingPage />
      </Lazy>
    );
  }
  return (
    <RequireAuth>
      <AppLayout />
    </RequireAuth>
  );
}

/** The onboarding route itself: session required, registration not. */
function RequireSession({ children }: { children: ReactNode }) {
  const { isAuthenticated, needsApproval, needsOnboarding, isLoading } = useAuth();

  if (isLoading) return <FullScreenLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (needsApproval) return <WaitingApproval />;
  if (!needsOnboarding) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/legal', element: <Lazy><LegalPage /></Lazy> },
  // Public demo of the sign-up flow: creates nothing, no session required.
  { path: '/demo', element: <Lazy><OnboardingPage demo /></Lazy> },
  {
    path: '/onboarding',
    element: (
      <RequireSession>
        <Lazy>
          <OnboardingPage />
        </Lazy>
      </RequireSession>
    ),
  },
  {
    // Full-screen flows live outside the tab bar layout on purpose.
    path: '/antojo',
    element: (
      <RequireAuth>
        <Lazy>
          <CravingPage />
        </Lazy>
      </RequireAuth>
    ),
  },
  {
    path: '/emergencia',
    element: (
      <RequireAuth>
        <Lazy>
          <EmergencyPage />
        </Lazy>
      </RequireAuth>
    ),
  },
  {
    // The admin panel is a full-screen control panel, not a tab-bar page.
    path: '/admin',
    element: (
      <RequireAuth>
        <Lazy>
          <AdminPage />
        </Lazy>
      </RequireAuth>
    ),
  },
  {
    path: '/',
    element: <RootGate />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'tratamiento', element: <Lazy><TreatmentPage /></Lazy> },
      { path: 'etapa', element: <Lazy><StagePage /></Lazy> },
      { path: 'mas', element: <Lazy><MorePage /></Lazy> },
      { path: 'salud', element: <Lazy><HealthPage /></Lazy> },
      { path: 'progreso', element: <Lazy><ProgressPage /></Lazy> },
      { path: 'dinero', element: <Lazy><MoneyPage /></Lazy> },
      { path: 'diario', element: <Lazy><JournalPage /></Lazy> },
      { path: 'avisos', element: <Lazy><NotificationsPage /></Lazy> },
      { path: 'ajustes', element: <Lazy><SettingsPage /></Lazy> },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
